import { z } from 'zod';

// User defined field schema - flexible key-value pairs
const UserFieldSchema = z.record(z.string(), z.string());

// Line item schema for Level 3 transaction data
const LineItemSchema = z.object({
  description: z.string()
    .min(1, { message: "Item description is required" })
    .max(26, { message: "Description cannot exceed 26 characters" }),
  discamnt: z.string()
    .max(12, { message: "Discount amount cannot exceed 12 characters" })
    .optional(),
  lineno: z.string()
    .max(4, { message: "Line number cannot exceed 4 characters" })
    .optional(),
  material: z.string()
    .max(12, { message: "Material code cannot exceed 12 characters" })
    .optional(),
  netamnt: z.string()
    .min(1, { message: "Net amount is required" })
    .max(12, { message: "Net amount cannot exceed 12 characters" }),
  quantity: z.string()
    .min(1, { message: "Quantity is required" })
    .max(12, { message: "Quantity cannot exceed 12 characters" }),
  taxamnt: z.string()
    .max(12, { message: "Tax amount cannot exceed 12 characters" })
    .optional(),
  unitcost: z.string()
    .min(1, { message: "Unit cost is required" })
    .max(12, { message: "Unit cost cannot exceed 12 characters" }),
  uom: z.string()
    .min(1, { message: "Unit of measure is required" })
    .max(8, { message: "Unit of measure cannot exceed 8 characters" }),
  upc: z.string()
    .min(1, { message: "UPC is required" })
    .max(14, { message: "UPC cannot exceed 14 characters" })
    .refine(val => val !== '000000000000' && !val.split('').every(char => char === '0'), {
      message: "UPC cannot be all zeros"
    }),
}).strict();

// Main authorization request schema
const AuthRequestSchema = z.object({
  // Idempotency key - for duplicate prevention (provided by client or auto-generated by middleware)
  idempotencyKey: z.string()
    .min(16, { message: "Idempotency key must be at least 16 characters" })
    .max(128, { message: "Idempotency key cannot exceed 128 characters" })
    .optional(),

  // Core transaction fields - merchid resolved server-side from restaurant's merchantId
  merchid: z.string()
    .max(16, { message: "Merchant ID cannot exceed 16 characters" })
    .optional(),
  account: z.string()
    .min(1, { message: "Account cannot be empty when provided" })
    .max(27, { message: "Account cannot exceed 27 characters" })
    .optional(), // Optional when profile is provided
  amount: z.string()
    .min(1, { message: "Amount is required" })
    .max(14, { message: "Amount cannot exceed 14 characters" }),
  authcode: z.string()
    .max(6, { message: "Auth code cannot exceed 6 characters" })
    .optional(),
  capture: z.enum(['Y', 'N'], { message: "Capture must be Y or N" })
    .optional(),
  tokenize: z.enum(['Y', 'N'], { message: "Tokenize must be Y or N" })
    .optional(),
  orderid: z.string()
    .max(50, { message: "Order ID cannot exceed 50 characters" })
    .optional(),
  currency: z.string()
    .length(3, { message: "Currency must be exactly 3 characters" })
    .optional(),

  // Payment method fields
  expiry: z.string()
    .max(8, { message: "Expiry cannot exceed 8 characters" })
    .regex(/^(\d{4}|\d{6}|\d{8})$/, { message: "Expiry must be MMYY, YYYYMM, or YYYYMMDD format" })
    .optional(),
  cvv2: z.string()
    .max(4, { message: "CVV2 cannot exceed 4 characters" })
    .regex(/^\d{3,4}$/, { message: "CVV2 must be 3 or 4 digits" })
    .optional(),
  track: z.string()
    .max(76, { message: "Track data cannot exceed 76 characters" })
    .optional(),

  // E-commerce and COF fields
  ecomind: z.enum(['T', 'R', 'E'], { message: "E-commerce indicator must be T, R, or E" })
    .optional(),
  cof: z.enum(['C', 'M'], { message: "Card on file must be C (Customer) or M (Merchant)" })
    .optional(),
  cofpermission: z.enum(['Y', 'N'], { message: "COF permission must be Y or N" })
    .optional(),
  cofscheduled: z.enum(['Y', 'N'], { message: "COF scheduled must be Y or N" })
    .optional(),

  // ACH fields
  bankaba: z.string()
    .length(9, { message: "Bank ABA must be exactly 9 characters" })
    .regex(/^\d{9}$/, { message: "Bank ABA must be 9 digits" })
    .optional(),
  achEntryCode: z.enum(['CCD', 'PPD', 'TEL', 'WEB'], { message: "ACH entry code must be CCD, PPD, TEL, or WEB" })
    .optional(),
  achDescription: z.string()
    .max(10, { message: "ACH description cannot exceed 10 characters" })
    .optional(),
  accttype: z.string()
    .max(6, { message: "Account type cannot exceed 6 characters" })
    .optional(),

  // Address verification fields
  postal: z.string()
    .max(10, { message: "Postal code cannot exceed 10 characters" })
    .optional(),
  address: z.string()
    .max(30, { message: "Address cannot exceed 30 characters" })
    .optional(),
  address2: z.string()
    .max(30, { message: "Address line 2 cannot exceed 30 characters" })
    .optional(),
  city: z.string()
    .max(30, { message: "City cannot exceed 30 characters" })
    .optional(),
  region: z.string()
    .max(20, { message: "Region cannot exceed 20 characters" })
    .optional(),
  country: z.string()
    .length(2, { message: "Country must be exactly 2 characters" })
    .regex(/^[A-Z]{2}$/, { message: "Country must be 2 uppercase letters" })
    .optional(),

  // Contact information
  name: z.string()
    .max(30, { message: "Name cannot exceed 30 characters" })
    .optional(),
  company: z.string()
    .max(128, { message: "Company cannot exceed 128 characters" })
    .optional(),
  phone: z.string()
    .max(15, { message: "Phone cannot exceed 15 characters" })
    .optional(),
  email: z.string()
    .email({ message: "Invalid email format" })
    .max(128, { message: "Email cannot exceed 128 characters" })
    .optional(),

  // Response options
  receipt: z.enum(['Y', 'N', 'json'], { message: "Receipt must be Y, N, or json" })
    .optional(),
  bin: z.enum(['Y', 'N'], { message: "BIN lookup must be Y or N" })
    .optional(),

  // Profile management
  profile: z.string()
    .max(24, { message: "Profile cannot exceed 24 characters" })
    .optional(),
  auoptout: z.enum(['Y', 'N'], { message: "Account updater opt-out must be Y or N" })
    .optional(),

  // Signature and terminal
  signature: z.string()
    .max(6144, { message: "Signature data cannot exceed 6144 characters" })
    .optional(),
  termid: z.string()
    .length(8, { message: "Terminal ID must be exactly 8 characters" })
    .regex(/^.{3}\d{5}$/, { message: "Terminal ID must end with 5 digits" })
    .optional(),

  // Level 2 transaction data
  taxexempt: z.enum(['Y', 'N'], { message: "Tax exempt must be Y or N" })
    .optional(),
  ponumber: z.string()
    .max(36, { message: "PO number cannot exceed 36 characters" })
    .optional(),
  taxamnt: z.string()
    .max(12, { message: "Tax amount cannot exceed 12 characters" })
    .optional(),

  // Level 3 transaction data
  frtamnt: z.string()
    .max(12, { message: "Freight amount cannot exceed 12 characters" })
    .optional(),
  dutyamnt: z.string()
    .max(12, { message: "Duty amount cannot exceed 12 characters" })
    .optional(),
  orderdate: z.string()
    .length(8, { message: "Order date must be exactly 8 characters" })
    .regex(/^\d{8}$/, { message: "Order date must be in YYYYMMDD format" })
    .optional(),
  shiptozip: z.string()
    .max(12, { message: "Ship to zip cannot exceed 12 characters" })
    .optional(),
  shipfromzip: z.string()
    .max(12, { message: "Ship from zip cannot exceed 12 characters" })
    .optional(),
  shiptocountry: z.string()
    .length(2, { message: "Ship to country must be exactly 2 characters" })
    .regex(/^[A-Z]{2}$/, { message: "Ship to country must be 2 uppercase letters" })
    .optional(),
  items: z.array(LineItemSchema)
    .optional(),

  // User defined fields
  userfields: z.array(UserFieldSchema)
    .optional(),
  invoiceid: z.string()
    .max(12, { message: "Invoice ID cannot exceed 12 characters" })
    .optional(),

  // 3D Secure fields
  securedstid: z.string()
    .max(36, { message: "Secure DSTID cannot exceed 36 characters" })
    .optional(),
  secureexemption: z.enum(['ivr', 'lowrisk', 'lowvalue', 'trusted'], {
    message: "Secure exemption must be ivr, lowrisk, lowvalue, or trusted"
  })
    .optional(),
  secureflag: z.enum(['05', '06'], { message: "Secure flag must be 05 or 06" })
    .optional(),
  securevalue: z.string()
    .max(32, { message: "Secure value cannot exceed 32 characters" })
    .optional(),
}).strict()
  .refine((data) => {
    // Validation: If COF is set, cofscheduled should be provided for MIT transactions
    if (data.cof === 'M' && !data.cofscheduled) {
      return false;
    }
    return true;
  }, {
    message: "cofscheduled is required for merchant-initiated transactions (cof: M)",
    path: ["cofscheduled"]
  })
  .refine((data) => {
    // Validation: Either account OR profile must be provided
    if (!data.account && !data.profile) {
      return false;
    }
    return true;
  }, {
    message: "Either account or profile is required for authorization",
    path: ["account"]
  })
  .refine((data) => {
    // Validation: ACH transactions require bankaba when account is provided
    if (data.accttype && ['ECHK', 'ESAV'].includes(data.accttype) && data.account && !data.bankaba) {
      return false;
    }
    return true;
  }, {
    message: "bankaba is required for ACH transactions when account is provided",
    path: ["bankaba"]
  })
  .refine((data) => {
    // Validation: US postal codes must be 5 or 10 digits
    if (data.country === 'US' && data.postal && !/^\d{5}(-?\d{4})?$/.test(data.postal)) {
      return false;
    }
    return true;
  }, {
    message: "US postal codes must be 5 or 10 digits (with optional hyphen)",
    path: ["postal"]
  })
  .refine((data) => {
    // Validation: US ship to zip codes must be 5 or 10 digits
    if (data.shiptocountry === 'US' && data.shiptozip && !/^\d{5}(-?\d{4})?$/.test(data.shiptozip)) {
      return false;
    }
    return true;
  }, {
    message: "US ship to zip codes must be 5 or 10 digits (with optional hyphen)",
    path: ["shiptozip"]
  })
  .refine((data) => {
    // Validation: Tax exempt validation
    if (data.taxexempt === 'Y' && data.taxamnt && parseFloat(data.taxamnt) !== 0) {
      return false;
    }
    if (data.taxexempt === 'N' && data.taxamnt && parseFloat(data.taxamnt) === 0) {
      return false;
    }
    return true;
  }, {
    message: "Tax amount must be zero if tax exempt is Y, and non-zero if tax exempt is N",
    path: ["taxamnt"]
  })
  .refine((data) => {
    // Validation: User fields total length limit (4000 bytes)
    if (data.userfields) {
      const totalLength = JSON.stringify(data.userfields).length;
      if (totalLength > 4000) {
        return false;
      }
    }
    return true;
  }, {
    message: "Total user fields cannot exceed 4000 bytes when JSON encoded",
    path: ["userfields"]
  })
// Note: Tax calculation validation removed - that's business logic, not schema validation

// Schema for creating an authorization request with required fields
const CreateAuthRequestSchema = AuthRequestSchema.safeExtend({
  merchid: z.string()
    .min(1, { message: "Merchant ID is required" })
    .max(16, { message: "Merchant ID cannot exceed 16 characters" }),
  account: z.string()
    .min(1, { message: "Account is required" })
    .max(27, { message: "Account cannot exceed 27 characters" }),
  amount: z.string()
    .min(1, { message: "Amount is required" })
    .max(14, { message: "Amount cannot exceed 14 characters" }),
});

// Schema for voice authorization requests
const VoiceAuthRequestSchema = CreateAuthRequestSchema.safeExtend({
  authcode: z.string()
    .min(1, { message: "Auth code is required for voice authorizations" })
    .max(6, { message: "Auth code cannot exceed 6 characters" }),
});

// Export type definitions
export type UserFieldType = z.infer<typeof UserFieldSchema>;
export type LineItemType = z.infer<typeof LineItemSchema>;
export type AuthRequestType = z.infer<typeof AuthRequestSchema>;
export type CreateAuthRequestType = z.infer<typeof CreateAuthRequestSchema>;
export type VoiceAuthRequestType = z.infer<typeof VoiceAuthRequestSchema>;

export default AuthRequestSchema;
export {
  CreateAuthRequestSchema, LineItemSchema, UserFieldSchema, VoiceAuthRequestSchema
};

